import { Shield } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { signIn } from '@/lib/auth'
import { useAuthStore } from '@/stores/auth-store'

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { setNeedsTwoFactor } = useAuthStore()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await signIn.email({ email, password })

      if (result.error) {
        setError(result.error.message ?? 'Sign in failed')
        setLoading(false)
        return
      }

      const data = result.data as Record<string, unknown> | null
      if (data?.twoFactorRedirect) {
        setNeedsTwoFactor(true)
        navigate('/2fa/verify', { replace: true })
        return
      }

      navigate('/', { replace: true })
    } catch {
      setError('An unexpected error occurred')
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center justify-center rounded-full border border-dashed border-border bg-surface p-3">
            <Shield className="h-6 w-6 text-accent" strokeWidth={1.5} />
          </div>
          <h1 className="font-mono text-xs font-medium tracking-widest text-ink uppercase">
            LookBookIQ Console
          </h1>
          <p className="mt-1 font-mono text-[11px] text-ink-faint">Admin access only</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-lg border border-dashed border-border bg-surface p-6"
        >
          {error && (
            <div className="mb-4 rounded-md border border-dashed border-negative/30 bg-negative/5 px-3 py-2 font-mono text-xs text-negative">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block font-mono text-[11px] tracking-wider text-ink-faint uppercase"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="mt-1.5 w-full rounded-md border border-dashed border-border bg-canvas px-3 py-2.5 font-mono text-xs text-ink placeholder:text-ink-faint"
                placeholder="admin@lookbookiq.app"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block font-mono text-[11px] tracking-wider text-ink-faint uppercase"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-1.5 w-full rounded-md border border-dashed border-border bg-canvas px-3 py-2.5 font-mono text-xs text-ink placeholder:text-ink-faint"
                placeholder="Enter password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-accent py-2.5 font-mono text-xs font-medium text-white transition-colors hover:bg-accent-hover disabled:opacity-50"
            >
              {loading ? 'Authenticating...' : 'Sign In'}
            </button>
          </div>
        </form>

        <p className="mt-4 text-center font-mono text-[10px] text-ink-faint">
          Unauthorized access is prohibited and monitored.
        </p>
      </div>
    </div>
  )
}
